import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/core/services/app.service';
import { FocuslinkService } from 'src/app/core/services/focuslink.service';
import { ImageCropperService } from 'src/app/core/services/image-cropper.service';
import { UserService } from 'src/app/core/services/user.service';
import { OOVenueService } from 'src/app/core/services/venue-service';
import { MenuObjectReorderRequest } from 'src/app/shared/models/DTO/MenuObjectReorderRequest.dto';
import { AppMessageSeverity } from 'src/app/shared/models/enums/AppMessageSeverity.enum';
import { DataSource } from 'src/app/shared/models/enums/data-source.enum';
import { OOCanvas } from 'src/app/shared/models/oo-canvas';
import { OOCanvasMenuItem } from 'src/app/shared/models/oo-canvas-menu-item.model';
import { OOMenuItem } from 'src/app/shared/models/oo-menu-item';
import { OOCanvasTimeRange } from 'src/app/shared/models/OOCanvasTimeRange.entity';

@Component({
	selector: 'app-canvas-record-setup',
	templateUrl: './canvas-record-setup.component.html',
	styleUrls: ['../../../commandcenter.scss'],
})
export class CanvasRecordSetupComponent implements OnInit, OnDestroy {
	@Input() canvasKey: number;
	canvas: OOCanvas;
	canvasIndex: number;
	@Input() duplicateCanvasKey: number;
	@Input() ooCanvasMenuItems: OOCanvasMenuItem[];
	@Output() canvasRecordSaveEmitter: EventEmitter<OOCanvas> = new EventEmitter<OOCanvas>();
	@Output() canvasRecordDeletedEmitter: EventEmitter<number> = new EventEmitter<number>();
	@Output() canvasRecordClearEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

	canvasItemMultiActionButton = [
		{
			label: 'Toggle Online',
			icon: 'pi pi-power-off',
			command: async () => {
				await this.toggleAllOnlineOffline(true);
			},
		},
		{
			label: 'Toggle Offline',
			icon: 'pi pi-power-off',
			command: async () => {
				await this.toggleAllOnlineOffline(false);
			},
		},
		{
			label: 'Toggle Tax On',
			icon: 'pi pi-percentage',
			command: async () => {
				await this.toggleAllTaxOnOff(true);
			},
		},
		{
			label: 'Toggle Tax Off',
			icon: 'pi pi-percentage',
			command: async () => {
				await this.toggleAllTaxOnOff(false);
			},
		},
		{
			label: 'Set Price',
			icon: 'pi pi-dollar',
			command: () => {
				this.togglePrice = true;
			},
		},
	];

	canvasSubscription = new Subscription();

	canvasUnsaved: boolean = false;
	changesToSave: boolean = false;

	duplicateOoCanvasMenuItemDetected: boolean = false;

	editRow: number;

	lastSave: Date;
	loadComplete: boolean = false;

	menuItemSelected: OOMenuItem;
	moment: any = moment;

	newCanvas: boolean = false;
	newRecord: boolean = false;

	ooCanvasMenuItemSelected: OOCanvasMenuItem;
	ooCanvasMenuItemToSaveList: OOCanvasMenuItem[] = [];

	priceNumberList: { label: string; value: number }[] = [];

	timeRangeListLoadingPlaceholder: string = 'Loading...';

	timestamp = new Date().getTime();

	togglePrice: boolean = false;
	togglePriceValueSelection: number;

	venueLocation: string;

	viewTab: number = 0;

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		private spinner: NgxSpinnerService,
		public imgCrop: ImageCropperService,
		public confirmationService: ConfirmationService,
		public venueSvc: OOVenueService,
		public userSvc: UserService,
		private activatedRoute: ActivatedRoute,
		private router: Router
	) {}

	ngOnInit(): void {
		this.canvasKey = +this.activatedRoute.snapshot.paramMap.get('canvasKey');
		this.canvas = this.venueSvc.ooCanvases.find((i) => i.canvasKey === this.canvasKey);
		if (this.canvas) {
			if (this.canvas.sharedFrom) {
				this.loadSharedCanvas(this.canvasKey);
			} else {
				this.loadCanvasData(this.canvasKey);
			}
		}
	}

	ngOnDestroy() {
		this.canvasSubscription.unsubscribe();
	}

	async loadCanvasData(canvasKey: number) {
		this.ooCanvasMenuItems = this.venueSvc.ooCanvases.find((i) => i.canvasKey === this.canvas.canvasKey).menuItems.filter((i) => i.menuItemKey);

		this.ooCanvasMenuItems?.forEach((mi) => {
			const ooMenuItem = this.venueSvc.ooMenuItems.find((i) => i.menuItemKey === mi.menuItemKey);
			mi.ooMenuItem = ooMenuItem;
			if (!ooMenuItem) {
				console.log(`Menu Item ${mi.menuItemKey} not found in OO Menu Items`);
				console.log(mi);
			}
		});
		this.ooCanvasMenuItems = [...this.ooCanvasMenuItems.filter((i) => i.ooMenuItem)];
		this.ooCanvasMenuItems = this.venueSvc.sortForDisplay(this.ooCanvasMenuItems);
		if (this.canvas.OOCanvasTimeRange) {
			this.canvas.OOCanvasTimeRange.forEach((ctr) => {
				ctr.name = this.venueSvc.timeRanges.find((tr) => tr.key === ctr.timeRangeKey).name;
			});
		}

		// this.canvasSubscription.add(
		// 	this.venueSvc.subscriptionOOcanvas.subscribe((data) => {
		// 		this.canvas = data?.find((i) => i.canvasKey === this.canvasKey);
		// 		if (this.canvas && this.canvas.menuItems?.length > 0) {
		// 			this.ooCanvasMenuItems = [...this.canvas.menuItems];
		// 		}
		// 	})
		// );
		this.timeRangeListLoadingPlaceholder = 'Select Available Times (if applicable)';

		for (let i = 1; i < 11; i++) {
			this.priceNumberList.push({ label: `Price ${i}`, value: i });
		}

		this.canvasIndex = this.venueSvc.ooCanvases.findIndex((canvas) => canvas.canvasKey === this.canvas.canvasKey);
		this.canvasSubscription.add(this.imgCrop.imageCroppingComplete.subscribe((change) => (this.timestamp = change)));
		this.loadComplete = true;
	}

	async loadSharedCanvas(canvasKey: number) {
		const canvas = await this.focuslink.canvasGet(this.venueSvc.subscriptionVenue.value.key, canvasKey, false);
		this.canvas = canvas;
		this.canvas.sharedFrom = `${canvas.venueKey}_${canvas.canvasKey}`;
		this.canvas.canvasKey = canvasKey;
		this.canvas.venueKey = this.venueSvc.subscriptionVenue.value.key;

		this.ooCanvasMenuItems = this.canvas?.ooCanvasMenuItems;
		this.loadComplete = true;
	}

	async canvasDelete(rec: OOCanvas) {
		this.router.navigate(['/cc/menu/canvas']);
		this.venueSvc.canvasDelete(rec.canvasKey);
	}

	async canvasRemoveImage(canvasKey: number) {
		this.canvas.imageKey = null;
		await this.focuslink.canvasPost(this.venueSvc.venueKey, this.canvas);
	}

	async duplicateCanvas(sourceCanvasKey: number) {
		const ooCanvas = this.venueSvc.ooCanvases.find((i) => i.canvasKey === this.duplicateCanvasKey);
		this.canvas.description = ooCanvas.description;
		this.canvas.sortOrder = ooCanvas.sortOrder;
		this.canvas.displayName = ooCanvas.displayName;
		this.canvas.max = ooCanvas.max;
		this.canvas.min = ooCanvas.min;
		this.canvas.online = ooCanvas.online;

		const ooCanvasMenuItems: OOCanvasMenuItem[] = ooCanvas.menuItems.length > 0 ? [...ooCanvas.menuItems] : [];
		ooCanvasMenuItems.forEach((i) => {
			i.canvasKey = this.canvas.canvasKey;
			i.venueKey = this.venueSvc.venueKey;
			i.unsaved = true;
			i.ooMenuItem = this.venueSvc.ooMenuItems.find((item) => item.menuItemKey === i.menuItemKey);
		});
		this.ooCanvasMenuItems = ooCanvasMenuItems;
	}

	itemsToSave(): boolean {
		return this.ooCanvasMenuItems.some((i) => i.unsaved);
	}

	menuItemPriceValue(value: number) {
		if (value >= 0) {
			value = +value;
			return ` - $${value.toFixed(2)}`;
		} else {
			return '';
		}
	}

	obtainNewCanvasKey() {
		const canvasKeys = this.venueSvc.ooCanvases.map((c) => c.canvasKey).sort((a, b) => b - a);
		const canvasSortValues = this.venueSvc.ooCanvases.map((c) => c.sortOrder).sort((a, b) => b - a);
		let canvasKey = 100001;
		if (canvasKeys.length > 0 && canvasKeys[0] > 100000) {
			canvasKey = canvasKeys[0] + 1;
		}
		return canvasKey;
	}

	async ooCanvasMenuItemAdd() {
		if (!this.ooCanvasMenuItems) {
			this.ooCanvasMenuItems = [];
		}
		this.ooCanvasMenuItemSelected = new OOCanvasMenuItem({
			venueKey: this.venueSvc.venueKey,
			canvasKey: this.canvas.canvasKey,
			unsaved: true,
			online: true,
			taxable: true,
			priceNumber: 1,
			source: DataSource.COMMANDCENTER,
		});
	}

	async ooCanvasMenuItemGet(venueKey, canvasKey) {
		const resp = await this.focuslink.canvasMenuItemGet(venueKey, canvasKey);
		const ooCanvasMenuItems = resp.map((i) => {
			const x = new OOCanvasMenuItem(i);

			if (x.quickAdd) {
				if (x.ooMenuItem.priceLevel || (x.ooMenuItem.ooMenuItemCanvases && x.ooMenuItem.ooMenuItemCanvases.length > 0)) {
					x.quickAdd = false;
				}
			}
			return x;
		});

		ooCanvasMenuItems.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));
		return ooCanvasMenuItems;
	}

	async onNewEditOOCanvasMenuItemReturn(rec: OOCanvasMenuItem) {
		this.ooCanvasMenuItemSelected = undefined;

		if (this.newRecord) {
			this.ooCanvasMenuItems.push(rec);
		} else {
			rec.unsaved = false;
			const idx = this.ooCanvasMenuItems.findIndex((i) => i.menuItemKey === rec.menuItemKey);
			if (idx > -1) {
				this.ooCanvasMenuItems[idx] = rec;
			}
		}
		this.newRecord = false;
	}

	async onCanvasNameChange() {
		const exists = this.venueSvc.ooCanvases.some((canvas) => canvas.canvasKey === this.canvas.canvasKey);
		if (!exists) {
			this.save(true);
		}
		this.venueSvc.canvaseSave(this.canvas);
		this.canvasUnsaved = false;
	}

	async onOOCanvasMenuItemRemove(rec: OOCanvasMenuItem) {
		await this.focuslink.canvasMenuItemDelete(this.venueSvc.venueKey, rec.canvasKey, rec.menuItemKey);
		const idx = this.ooCanvasMenuItems.findIndex((i) => i.menuItemKey === rec.menuItemKey && i.canvasKey === rec.canvasKey);
		this.ooCanvasMenuItems.splice(idx, 1);
		this.ooCanvasMenuItems = [...this.ooCanvasMenuItems];
	}

	async onOOCanvasItemReorder() {
		this.app.loading(true);
		try {
			this.ooCanvasMenuItems.forEach((cmi, idx) => {
				cmi.sortOrder = idx + 1;
			});
			await this.focuslink.ooMenuReorderObject(
				this.venueSvc.venueKey,
				'cmi',
				this.ooCanvasMenuItems.map((i) => new MenuObjectReorderRequest({ key: i.menuItemKey, sortOrder: i.sortOrder })),
				this.canvas.canvasKey
			);

			this.ooCanvasMenuItems = this.venueSvc.sortForDisplay(this.ooCanvasMenuItems);
			this.app.loading(false);
			this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `Item resorted` });
		} catch (error) {
			this.app.loading(false);
			this.app.msgSvc.add({ severity: AppMessageSeverity.RED, summary: '', detail: `Error resorting items. Sorry.` });
		}
	}

	async onAddItemClear() {
		this.ooCanvasMenuItemSelected = undefined;
		this.newRecord = false;
		this.ooCanvasMenuItems = [...this.ooCanvasMenuItems];
	}

	onClose() {
		this.router.navigate(['/cc/menu/canvas']);
	}

	async returnFromMenuItemInspector(rec: OOMenuItem) {
		const idx = this.ooCanvasMenuItems.findIndex((i) => i.menuItemKey === rec.menuItemKey);
		this.ooCanvasMenuItems[idx] = rec.ooCanvasMenuItems.find((i) => i.canvasKey === this.canvas.canvasKey);
		this.menuItemSelected = undefined;
		this.ooCanvasMenuItems = [...this.ooCanvasMenuItems];
	}

	async save(noClear?: boolean) {
		if (this.ooCanvasMenuItems?.some((i) => i.unsaved === true)) {
			this.ooCanvasMenuItemToSaveList = this.ooCanvasMenuItems.filter((i) => i.unsaved);
		}
		await this.venueSvc.canvaseSave(this.venueSvc.ooCanvases.find((i) => i.canvasKey === this.canvas.canvasKey));

		if (this.ooCanvasMenuItemToSaveList.length > 0) {
			this.ooCanvasMenuItemToSaveList.forEach((cmi) => {
				this.venueSvc.canvasMenuItemSave(cmi);
			});
		}

		if (noClear) return;
	}

	saveCanvasMenuItem(cmi: OOCanvasMenuItem) {
		this.venueSvc.canvasMenuItemSave(cmi);
	}

	async selectCanvasImage(canvasKey) {
		this.imgCrop.openImageCropper('canvas', canvasKey, this.venueSvc.venueKey);
		this.canvas.imageKey = `canvas-${canvasKey}.png`;
	}

	async setOOMenuItem(menuItemKey) {
		this.duplicateOoCanvasMenuItemDetected = this.ooCanvasMenuItems.some((i) => i.menuItemKey === this.ooCanvasMenuItemSelected.menuItemKey) ? true : false;
		if (!this.duplicateOoCanvasMenuItemDetected) {
			this.ooCanvasMenuItemSelected.ooMenuItem = this.venueSvc.ooMenuItems.find((i) => i.menuItemKey === menuItemKey);
		}
	}

	onTabChange(event) {
		if (this.canvasUnsaved && this.newCanvas) {
			this.save();
		}
	}

	timeRangeSelect(event) {
		if (!this.canvas.OOCanvasTimeRange) {
			this.canvas.OOCanvasTimeRange = [];
		}
		this.canvas.OOCanvasTimeRange.push(
			new OOCanvasTimeRange({
				venueKey: this.venueSvc.venueKey,
				canvasKey: this.canvas.canvasKey,
				timeRangeKey: event.value,
				name: this.venueSvc.timeRanges.find((i) => i.key === event.value).name,
			})
		);
	}

	timeRangeRemove(key) {
		const idx = this.canvas.OOCanvasTimeRange.findIndex((i) => i.timeRangeKey === key);
		this.canvas.OOCanvasTimeRange.splice(idx, 1);
	}

	async toggleAllOnlineOffline(online: boolean) {
		this.ooCanvasMenuItems.forEach((i) => {
			i.online = online;
			this.venueSvc.canvasMenuItemSave(i);
		});
	}

	async toggleAllTaxOnOff(taxable: boolean) {
		this.ooCanvasMenuItems.forEach((i) => {
			i.taxable = taxable;
			this.venueSvc.canvasMenuItemSave(i);
		});
	}

	async setAllPrice(priceNumber: number) {
		this.ooCanvasMenuItems.forEach((i) => {
			i.priceNumber = priceNumber;
			this.venueSvc.canvasMenuItemSave(i);
		});
	}

	validateForSave() {
		if (!this.ooCanvasMenuItems || this.ooCanvasMenuItems.length === 0) {
			this.app.confirmation.confirm({
				message: `You are saving a canvas without items, are you sure you want to continue?`,
				accept: async () => {
					this.save();
					this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `Canvas saved successfully.` });
				},
				reject: async () => {
					this.app.confirmation.close();
					return;
				},
			});
		} else {
			this.save();
			this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: '', detail: `Canvas saved successfully.` });
		}
	}
}
